import React from 'react';
import { Save, FileCheck, Send, AlertTriangle, Info, X } from 'lucide-react';

export type ReportAction = 'draft' | 'ready' | 'submit';

interface ReportActionModalProps {
    isOpen: boolean;
    mode: ReportAction | null;
    onClose: () => void;
    onConfirm: () => void;
    isSubmitting: boolean;
    /** Report/batch title shown in the context box. */
    title?: string;
    /** Secondary context line (e.g. the period label). */
    subtitle?: string;
    /** Small summary line (e.g. "12 records selected"). */
    detail?: string;
    /** Total record count to show (e.g. the resulting number of records in the batch). */
    records?: number;
    /** Change summary shown under a bold "Changes:" label (e.g. "0 added, 1 removed"). */
    changes?: string;
}

const MODE_CONFIG: Record<ReportAction, {
    header: string;
    Icon: React.ComponentType<{ className?: string }>;
    title: string;
    tagline: string;
    calloutBox: string;
    CalloutIcon: React.ComponentType<{ className?: string }>;
    calloutIcon: string;
    calloutText: string;
    calloutTextColor: string;
    confirm: string;
    confirmLabel: string;
    busyLabel: string;
}> = {
    draft: {
        header: 'bg-gradient-to-r from-gray-700 to-gray-800',
        Icon: Save,
        title: 'Save as Draft',
        tagline: 'Your progress will be saved',
        calloutBox: 'bg-blue-50 border-blue-100',
        CalloutIcon: Info,
        calloutIcon: 'text-blue-600',
        calloutText: 'This saves the report as a draft. You can keep adding or removing records and come back anytime — it will NOT be submitted for approval yet.',
        calloutTextColor: 'text-blue-700',
        confirm: 'bg-gray-800 hover:bg-gray-900',
        confirmLabel: 'Save Draft',
        busyLabel: 'Saving…',
    },
    ready: {
        header: 'bg-gradient-to-r from-blue-600 to-blue-700',
        Icon: FileCheck,
        title: 'Mark Ready for Submission',
        tagline: 'This finalises the report’s records',
        calloutBox: 'bg-amber-50 border-amber-200',
        CalloutIcon: AlertTriangle,
        calloutIcon: 'text-amber-600',
        calloutText: 'This marks the report ready for submission. Its records can NO LONGER be edited. You will still need to submit it for approval from the requests list as a separate step.',
        calloutTextColor: 'text-amber-700',
        confirm: 'bg-blue-600 hover:bg-blue-700',
        confirmLabel: 'Mark Ready',
        busyLabel: 'Saving…',
    },
    submit: {
        header: 'bg-gradient-to-r from-[#088A61] to-[#065f44]',
        Icon: Send,
        title: 'Submit for Approval',
        tagline: 'This sends the report for review',
        calloutBox: 'bg-amber-50 border-amber-200',
        CalloutIcon: AlertTriangle,
        calloutIcon: 'text-amber-600',
        calloutText: 'This sends the report to the approvals module for review. Once submitted it CANNOT be changed here anymore.',
        calloutTextColor: 'text-amber-700',
        confirm: 'bg-[#088A61] hover:bg-[#067451]',
        confirmLabel: 'Submit',
        busyLabel: 'Submitting…',
    },
};

const ReportActionModal: React.FC<ReportActionModalProps> = ({
    isOpen, mode, onClose, onConfirm, isSubmitting, title, subtitle, detail, records, changes,
}) => {
    if (!isOpen || !mode) return null;
    const cfg = MODE_CONFIG[mode];
    const { Icon, CalloutIcon } = cfg;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={!isSubmitting ? onClose : undefined} />
            <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div className={`px-6 py-5 ${cfg.header}`}>
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                                <Icon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h3 className="text-base font-black text-white">{cfg.title}</h3>
                                <p className="text-xs text-white/70 mt-0.5">{cfg.tagline}</p>
                            </div>
                        </div>
                        {!isSubmitting && (
                            <button onClick={onClose} className="text-white/60 hover:text-white transition-colors flex-shrink-0">
                                <X className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                </div>

                <div className="px-6 py-5 space-y-4">
                    {(title || subtitle || detail || typeof records === 'number' || changes) && (
                        <div className="bg-gray-50 border border-gray-100 rounded-lg px-4 py-3">
                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-0.5">Report</p>
                            {title && <p className="text-sm font-semibold text-gray-800 leading-snug line-clamp-2">{title}</p>}
                            {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
                            {detail && <p className="text-xs text-gray-500 mt-1">{detail}</p>}
                            {typeof records === 'number' && (
                                <p className="text-xs text-gray-500 mt-1">
                                    <span className="font-semibold text-gray-700">Total records:</span> {records}
                                </p>
                            )}
                            {changes && (
                                <p className="text-xs text-gray-500 mt-1">
                                    <span className="font-semibold text-gray-700">Changes:</span> {changes}
                                </p>
                            )}
                        </div>
                    )}

                    <div className={`flex items-start gap-3 border rounded-lg px-4 py-3 ${cfg.calloutBox}`}>
                        <CalloutIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${cfg.calloutIcon}`} />
                        <p className={`text-xs font-medium leading-relaxed ${cfg.calloutTextColor}`}>{cfg.calloutText}</p>
                    </div>

                    <div className="flex items-center gap-3 pt-1">
                        <button onClick={onClose} disabled={isSubmitting}
                            className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 font-semibold hover:bg-gray-50 transition-colors disabled:opacity-60">
                            Cancel
                        </button>
                        <button onClick={onConfirm} disabled={isSubmitting}
                            className={`flex-1 flex items-center justify-center gap-2 px-5 py-2.5 text-white rounded-xl text-sm font-bold transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed ${cfg.confirm}`}>
                            {isSubmitting
                                ? <><div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />{cfg.busyLabel}</>
                                : <><Icon className="w-4 h-4" />{cfg.confirmLabel}</>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReportActionModal;
